import { useMemo, useState } from "react";
import { ActivityIndicator, Pressable, Text, View } from "react-native";
import { useTranslation } from "react-i18next";

import { detectRirDrift } from "@jackedlogs/shared";

import { Icon } from "../Icon/Icon";
import { PanelTitleRow } from "./InfoPill";
import { DEFAULT_WINDOW_DAYS } from "./progressWindow";
import { makeTierTwoStyles } from "./tierTwoPanelStyles";
import type { ProgressApiFetch, ProgressDeps } from "./types";
import { useWindowSets } from "./useWindowSets";

// Lazy-fetch the last `days` of sets in a single /api/sets?since query.
// Self-only route (no athleteSub support) — the shared-window mechanics live
// in useWindowSets; this callsite only names its route + envelope shape.
const useRirSets = (apiFetch: ProgressApiFetch, days: number) =>
  useWindowSets(
    apiFetch,
    days,
    (since) => `/api/sets?since=${encodeURIComponent(since)}&limit=500`,
    (resp) => (resp as { items?: unknown[] }).items ?? [],
  );

// Rolling RPE vs RIR over 28 days. Reads sets carrying both rpe and rir;
// degrades gracefully to "no data" for sessions that pre-date the RIR input.
export function FatigueDriftPanel(deps: ProgressDeps) {
  const { theme, apiFetch } = deps;
  const windowDays = deps.windowDays ?? DEFAULT_WINDOW_DAYS;
  const { t, i18n } = useTranslation();
  const { sets, error } = useRirSets(apiFetch, windowDays);
  const styles = useMemo(() => makeTierTwoStyles(theme), [theme]);
  const [activeIdx, setActiveIdx] = useState<number | null>(null);
  const dateFmt = useMemo(
    () => new Intl.DateTimeFormat(i18n.language, { day: "2-digit", month: "short" }),
    [i18n.language],
  );
  const period = t("progress.period.days", {
    defaultValue: "{{days}} d",
    days: windowDays,
  });

  const drift = useMemo(() => {
    if (!sets) return null;
    return detectRirDrift(sets, Date.now(), windowDays);
  }, [sets, windowDays]);

  const title = t("mobile.progress.fatigueDrift.title", {
    defaultValue: "Fatigue drift (RIR)",
  });
  const help = t("mobile.progress.fatigueDrift.help", {
    defaultValue:
      "Rolling weekly average of RPE and reps-in-reserve on sets where both are logged. Rising RPE with falling RIR on similar loads = accumulated fatigue showing before the bar slows down. A backoff week or deload usually clears it.",
  });

  if (error) {
    return (
      <View style={[styles.card, styles.cardError]}>
        <PanelTitleRow theme={theme} title={title} body={help} period={period} />
        <Text style={styles.errorText}>
          {t("mobile.progress.loadFailed", { defaultValue: "Couldn’t load" })}
        </Text>
      </View>
    );
  }
  if (drift === null) {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={title} body={help} period={period} />
        <ActivityIndicator color={theme.palette.brand500} />
      </View>
    );
  }
  if (drift.flag === "no-data") {
    return (
      <View style={styles.card}>
        <PanelTitleRow theme={theme} title={title} body={help} period={period} />
        <Text style={styles.empty}>
          {t("mobile.progress.fatigueDrift.noData", {
            defaultValue:
              "Log RPE and RIR on a few sets to chart fatigue drift",
          })}
        </Text>
      </View>
    );
  }

  const flagLabel =
    drift.flag === "rising"
      ? t("mobile.progress.fatigueDrift.rising", {
          defaultValue: "Rising fatigue",
        })
      : t("mobile.progress.fatigueDrift.stable", { defaultValue: "Stable" });
  const flagColor =
    drift.flag === "rising" ? theme.palette.danger : theme.palette.ink3;

  return (
    <View style={styles.card}>
      <PanelTitleRow
        theme={theme}
        title={title}
        body={help}
        period={period}
        right={
          <View style={styles.flagRow}>
            <Icon
              name={drift.flag === "rising" ? "trend-up" : "arrow-right"}
              size={theme.typography.sizes.sm}
              color={flagColor}
            />
            <Text style={[styles.densitySummary, { color: flagColor }]}>
              {flagLabel}
            </Text>
          </View>
        }
      />
      <View style={styles.fatigueChart}>
        {drift.buckets.map((b, i) => {
          // RPE 1..10 → 10%..100%; RIR 0..10 → 0%..100%. Two adjacent bars
          // per week. Neutral by default; the latest week's RPE bar keeps the
          // brand orange as the "now" accent (colour budget). Tap a week to
          // read its values below the chart.
          const rpePct = Math.max(0.05, b.avgRpe / 10);
          const rirPct = Math.max(0.02, b.avgRir / 10);
          const isNow = i === drift.buckets.length - 1;
          const active = activeIdx === i;
          return (
            <Pressable
              key={`${b.bucketEndMs}-${i}`}
              style={styles.fatigueWeek}
              onPress={() => setActiveIdx(active ? null : i)}
              accessibilityRole="button"
              accessibilityState={{ selected: active }}
              accessibilityLabel={`${dateFmt.format(new Date(b.bucketEndMs))} · RPE ${b.avgRpe.toFixed(1)} · RIR ${b.avgRir.toFixed(1)}`}
            >
              <View style={styles.fatigueBarPair}>
                <View
                  style={[
                    styles.fatigueBarRpe,
                    {
                      height: `${rpePct * 100}%`,
                      backgroundColor: isNow ? theme.palette.brand500 : theme.palette.ink,
                    },
                  ]}
                />
                <View
                  style={[
                    styles.fatigueBarRir,
                    {
                      height: `${rirPct * 100}%`,
                      backgroundColor: theme.palette.ink3,
                    },
                  ]}
                />
              </View>
              <Text style={[styles.fatigueWeekLabel, active && { color: theme.palette.ink }]}>
                {b.sampleCount}
              </Text>
            </Pressable>
          );
        })}
      </View>
      {activeIdx !== null && drift.buckets[activeIdx] ? (
        <Text style={[styles.fatigueLegend, { fontStyle: "normal", color: theme.palette.ink2 }]}>
          {`${dateFmt.format(new Date(drift.buckets[activeIdx]!.bucketEndMs))} · RPE ${drift.buckets[activeIdx]!.avgRpe.toFixed(1)} · RIR ${drift.buckets[activeIdx]!.avgRir.toFixed(1)}`}
        </Text>
      ) : (
        <Text style={styles.fatigueLegend}>
          {t("mobile.progress.fatigueDrift.legend", {
            defaultValue: "RPE (left) · RIR (right) — tap a week for values",
          })}
        </Text>
      )}
    </View>
  );
}
